/**
 * OnboardingContinueButton — Round 218D P0 (2026-02-07).
 *
 * The single source of truth for every onboarding builder's
 * completion CTA. Drop this in ANY builder's success screen and it
 * will:
 *   1. Call GET /api/foundation/next-action (canonical resolver).
 *   2. Render a button labelled with the resolver's next_cta_label.
 *   3. Navigate to the resolver's next_route on click.
 *
 * Never hardcode a next-step label or route in a builder — always
 * mount this component. That keeps the onboarding chain (Offer →
 * Brand → Sales Page → Lead Magnet → Emails → Business Essentials
 * → Ebook → Find Viral Ideas → Create Content → Growth → Run My
 * Business → Run My Week → Dashboard) driven from ONE place.
 *
 * Post-onboarding, the resolver returns the "Open your Dashboard"
 * terminal step — this component simply renders that label. It
 * NEVER locks the customer; the surrounding builder is still free
 * to render its own "Back to Dashboard" secondary action.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import useNextAction from '@/hooks/useNextAction';

const OnboardingContinueButton = ({
  isOpen = true,
  testid = 'onboarding-continue-btn',
  className = '',
  variant = 'primary',
  onBeforeNavigate,
}) => {
  const nav = useNextAction(isOpen);
  const navigate = useNavigate();

  const label = nav.loading
    ? 'Loading next step…'
    : nav.nextCtaLabel || 'Continue';

  const disabled = nav.loading || !nav.nextRoute;

  const onClick = async () => {
    if (disabled) return;
    if (typeof onBeforeNavigate === 'function') {
      try { await onBeforeNavigate(nav); } catch (_) { /* noop */ }
    }
    navigate(nav.nextRoute);
  };

  const base =
    variant === 'primary'
      ? 'w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg disabled:opacity-70'
      : 'inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3 transition';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      data-next-step-id={nav.nextStepId || ''}
      data-next-route={nav.nextRoute || ''}
      className={`${base} ${className}`}
    >
      <span>{label}</span>
      <ArrowRight size={18} weight="bold" />
    </button>
  );
};

export default OnboardingContinueButton;
